import React from 'react'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'

// Shown on save when the editor holds option changes that aren't saved yet, in
// either of the two forms they take.
//
// `taxonomyCount` fields have option edits applied for testing: those live in a
// taxonomy of their own, so they can be persisted alongside this save or left
// alone — hence the choice.
//
// `switchedFieldCount` fields point at different options than the saved
// configuration does (a copy of their own in place of the org default's, or the
// default's back in place of a copy). Nothing separate to persist there — the
// configuration save carries the switch either way — so it's stated rather than
// asked, and on its own it needs no more than a confirmation.
export const UnsavedTaxonomyModal = ({
  show,
  onClose,
  onSavePromptOnly,
  onSaveWithTaxonomies,
  saving,
  taxonomyCount,
  switchedFieldCount = 0,
  blockedByGateCount = 0,
}) => {
  const fieldWord = (count) => (count === 1 ? 'field' : 'fields')
  const hasOptionEdits = taxonomyCount > 0
  // A condition reading an option that only exists in one of these edits has no
  // category uuid to send yet, so saving the prompt without the options would
  // post an edge the service rejects. The options have to go with it.
  const mustSaveOptions = blockedByGateCount > 0
  const switchedSentence =
    switchedFieldCount === 0
      ? null
      : `${switchedFieldCount} ${fieldWord(switchedFieldCount)} ${switchedFieldCount === 1 ? 'uses' : 'use'} different options than the saved configuration. Confirm you'd like to save and apply them along with any other changes.` // prettier-ignore

  return (
    <BasicModal show={show} onClose={onClose} title="Unsaved option changes" size="tiny">
      <div className="mb" data-testid="unsaved-taxonomy-body">
        {hasOptionEdits && (
          <p>
            You applied option changes to {taxonomyCount} {fieldWord(taxonomyCount)} for testing but
            haven&apos;t saved {taxonomyCount === 1 ? 'it' : 'them'}. Save the prompt on its own, or
            save the option changes too?
          </p>
        )}
        {switchedSentence && <p>{switchedSentence}</p>}
        {mustSaveOptions && (
          <p data-testid="unsaved-taxonomy-gate-note">
            {blockedByGateCount === 1 ? 'A condition uses' : `${blockedByGateCount} conditions use`}{' '}
            an option from those changes, so the options have to be saved with the prompt.
          </p>
        )}
      </div>

      <div className="modal-footer">
        <Button
          type="button"
          onClick={hasOptionEdits ? onSavePromptOnly : onClose}
          disabled={saving || (hasOptionEdits && mustSaveOptions)}
          disabledTooltip="A condition reads an option these changes add, so it has to be saved too"
          data-testid={hasOptionEdits ? 'save-prompt-only-button' : 'unsaved-taxonomy-cancel'}
        >
          {hasOptionEdits ? 'Save prompt only' : 'Cancel'}
        </Button>
        <Button
          primary
          type="button"
          onClick={hasOptionEdits ? onSaveWithTaxonomies : onSavePromptOnly}
          loading={saving}
          data-testid={hasOptionEdits ? 'save-with-taxonomies-button' : 'unsaved-taxonomy-save'}
        >
          {hasOptionEdits ? 'Save option changes too' : 'Save Changes'}
        </Button>
      </div>
    </BasicModal>
  )
}
